import React, { useCallback } from 'react';

import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  CompoundEntityRef,
  DEFAULT_NAMESPACE,
  stringifyEntityRef,
  parseEntityRef,
} from '@backstage/catalog-model';
import {
  AppIcon,
  HeaderIconLinkRow,
  IconLinkVerticalProps,
  InfoCardVariants,
  Link,
} from '@backstage/core-components';
import {
  alertApiRef,
  errorApiRef,
  useApi,
  useApp,
  useRouteRef,
} from '@backstage/core-plugin-api';
import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import {
  catalogApiRef,
  getEntitySourceLocation,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { usePermission } from '@backstage/plugin-permission-react';
import { isTemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { taskCreatePermission } from '@backstage/plugin-scaffolder-common/alpha';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import CachedIcon from '@material-ui/icons/Cached';
import DeleteIcon from '@material-ui/icons/Delete';
import DocsIcon from '@material-ui/icons/Description';
import EditIcon from '@material-ui/icons/Edit';

import { createFromTemplateRouteRef, viewTechDocRouteRef } from '../../routes';
import { AboutContent } from './AboutContent';
import { useSourceTemplateCompoundEntityRef } from './hooks';

const TECHDOCS_ANNOTATION = 'backstage.io/techdocs-ref';

const TECHDOCS_EXTERNAL_ANNOTATION = 'backstage.io/techdocs-entity';

const useStyles = makeStyles({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)', // for pages without content header
    marginBottom: '10px',
  },
  cardHeader: {
    paddingBottom: '5px',
    paddingLeft: '22px',
  },
  fullHeightCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  gridItemCardContent: {
    flex: 1,
  },
  fullHeightCardContent: {
    flex: 1,
  },
});

/**
 * Props for {@link EntityAboutCard}.
 *
 * @public
 */
export interface AboutCardProps {
  variant?: InfoCardVariants;
  /** Hide the edit button. Defaults to false. */
  hideEditButton?: boolean;
  /** Hide the refresh button. Defaults to false. */
  hideRefreshButton?: boolean;
  /** Hide the View Source link. Defaults to false. */
  hideSourceLinks?: boolean;
  /** Hide the View TechDocs link. Defaults to false. */
  hideTechDocsLinks?: boolean;
  /** Custom edit URL to override the ANNOTATION_EDIT_URL. Useful for linking to scaffolder templates. */
  editUrl?: string;
  /** URL to the delete/decommission scaffolder template. Button will be disabled if canDelete prop is false. */
  deleteTemplateUrl?: string;
  /** Additional links to add to the subheader. */
  additionalSubHeaderLinks?: IconLinkVerticalProps[];
  /**
   * When provided, controls whether the edit button is enabled.
   * If `undefined`, the edit button is enabled whenever `editUrl` is set (default behavior).
   * If `false`, the edit button is disabled regardless of `editUrl`.
   */
  canEdit?: boolean;
  /**
   * When provided, controls whether the delete button is enabled.
   * If `undefined`, the delete button is enabled (default behavior).
   * If `false`, the delete button is disabled regardless of `deleteTemplateUrl`.
   */
  canDelete?: boolean;
}

export function AboutCard(props: AboutCardProps) {
  const {
    variant,
    hideEditButton = false,
    hideRefreshButton = false,
    hideSourceLinks = false,
    hideTechDocsLinks = false,
    editUrl,
    deleteTemplateUrl,
    additionalSubHeaderLinks,
    canEdit,
    canDelete,
  } = props;
  const app = useApp();
  const classes = useStyles();
  const { entity } = useEntity();
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const viewTechdocLink = useRouteRef(viewTechDocRouteRef);
  const templateRoute = useRouteRef(createFromTemplateRouteRef);
  const sourceTemplateRef = useSourceTemplateCompoundEntityRef(entity);
  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );

  const { allowed: canCreateTemplateTask } = usePermission({
    permission: taskCreatePermission,
  });

  const entitySourceLocation = getEntitySourceLocation(
    entity,
    scmIntegrationsApi,
  );

  const entityMetadataEditUrl =
    editUrl ?? entity.metadata.annotations?.[ANNOTATION_EDIT_URL];

  let techdocsRef: CompoundEntityRef | undefined;

  if (entity.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION]) {
    try {
      techdocsRef = parseEntityRef(
        entity.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION],
      );
      // not a fan of this but we don't care if the parseEntityRef fails
    } catch {
      techdocsRef = undefined;
    }
  }

  const viewInSource: IconLinkVerticalProps = {
    label: 'View Source',
    disabled: !entitySourceLocation,
    icon: <ScmIntegrationIcon type={entitySourceLocation?.integrationType} />,
    href: entitySourceLocation?.locationTargetUrl,
  };
  const viewInTechDocs: IconLinkVerticalProps = {
    label: 'View TechDocs',
    disabled:
      !(
        entity.metadata.annotations?.[TECHDOCS_ANNOTATION] ||
        entity.metadata.annotations?.[TECHDOCS_EXTERNAL_ANNOTATION]
      ) || !viewTechdocLink,
    icon: <DocsIcon />,
    href:
      viewTechdocLink &&
      (techdocsRef
        ? viewTechdocLink({
            namespace: techdocsRef.namespace || DEFAULT_NAMESPACE,
            kind: techdocsRef.kind,
            name: techdocsRef.name,
          })
        : viewTechdocLink({
            namespace: entity.metadata.namespace || DEFAULT_NAMESPACE,
            kind: entity.kind,
            name: entity.metadata.name,
          })),
  };

  const subHeaderLinks = [
    ...(hideSourceLinks ? [] : [viewInSource]),
    ...(hideTechDocsLinks ? [] : [viewInTechDocs]),
    ...(additionalSubHeaderLinks ?? []),
  ];

  if (isTemplateEntityV1beta3(entity)) {
    const Icon = app.getSystemIcon('scaffolder') ?? CreateComponentIcon;

    const launchTemplate: IconLinkVerticalProps = {
      label: 'Launch Template',
      icon: <Icon />,
      disabled: !templateRoute || !canCreateTemplateTask,
      href:
        templateRoute &&
        templateRoute({
          templateName: entity.metadata.name,
          namespace: entity.metadata.namespace || DEFAULT_NAMESPACE,
        }),
    };

    subHeaderLinks.push(launchTemplate);
  }

  let cardClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
  }

  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardContentClass = classes.fullHeightCardContent;
  }

  const entityLocation = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  // Limiting the ability to manually refresh to the less expensive locations
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');
  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Refresh scheduled',
        severity: 'info',
        display: 'transient',
      });
    } catch (e) {
      errorApi.post(e as Error);
    }
  }, [catalogApi, entity, alertApi, errorApi]);

  return (
    <Card className={cardClass}>
      <CardHeader
        title="About"
        className={classes.cardHeader}
        action={
          <>
            {!hideRefreshButton && allowRefresh && canRefresh && (
              <IconButton
                aria-label="Refresh"
                title="Schedule entity refresh"
                onClick={refreshEntity}
              >
                <CachedIcon />
              </IconButton>
            )}
            {!hideEditButton && (
              <IconButton
                component={Link}
                aria-label="Edit"
                disabled={!entityMetadataEditUrl || canEdit === false}
                title="Edit Metadata"
                to={
                  entityMetadataEditUrl && canEdit !== false
                    ? entityMetadataEditUrl
                    : '#'
                }
              >
                <EditIcon />
              </IconButton>
            )}
            {sourceTemplateRef && templateRoute && (
              <IconButton
                component={Link}
                title="Create something similar"
                to={templateRoute({
                  namespace: sourceTemplateRef.namespace,
                  templateName: sourceTemplateRef.name,
                })}
              >
                <AppIcon id="scaffolder" />
              </IconButton>
            )}
            {deleteTemplateUrl && (
              <IconButton
                component={Link}
                aria-label="Delete"
                disabled={canDelete === false}
                title="Delete Entity"
                to={canDelete !== false ? deleteTemplateUrl : '#'}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </>
        }
        subheader={<HeaderIconLinkRow links={subHeaderLinks} />}
      />
      <Divider />
      <CardContent className={cardContentClass}>
        <AboutContent entity={entity} />
      </CardContent>
    </Card>
  );
}
